import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  GoogleLogo,
  Lightning,
  ArrowUpRight,
  YoutubeLogo,
  SpotifyLogo,
  GithubLogo,
  WhatsappLogo,
  Heart,
} from "@phosphor-icons/react";
import PolicyDialog, { hasAcceptedTerms } from "../components/PolicyDialog";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [policyOpen, setPolicyOpen] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(() => hasAcceptedTerms());

  useEffect(() => {
    if (!loading && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, loading, navigate]);

  const handleGoogleLogin = () => {
    if (!termsAccepted) {
      setPolicyOpen(true);
      return;
    }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleAcceptPolicy = () => {
    setPolicyOpen(false);
    setTermsAccepted(true);
    setTimeout(() => {
      const redirectUrl = window.location.origin + "/dashboard";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }, 250);
  };

  // Demo tiles shown as a teaser row inside the login card
  const demoTiles = [
    { Icon: YoutubeLogo, color: "#EF4444" },
    { Icon: SpotifyLogo, color: "#22C55E" },
    { Icon: GithubLogo, color: "#FFFFFF", iconColor: "#0A0B10" },
    { Icon: WhatsappLogo, color: "#25D366" },
    { Icon: Lightning, color: "#2563EB" },
    { Icon: Heart, color: "#FACC15", iconColor: "#0A0B10" },
  ];

  return (
    <div className="relative min-h-screen w-full overflow-hidden login-bg flex items-center justify-center px-6 py-10">
      <div className="noise-overlay" />

      {/* Subtle decorative tiles — smaller, less dominant */}
      <div className="hidden md:block pointer-events-none absolute inset-0">
        <div className="absolute top-[10%] left-[8%] w-20 h-20 rounded-2xl bg-[#2563EB] deck-tile flex items-center justify-center rotate-[-12deg] opacity-70">
          <Lightning size={32} weight="fill" className="text-white" />
        </div>
        <div className="absolute top-[18%] right-[10%] w-16 h-16 rounded-2xl bg-[#EF4444] deck-tile flex items-center justify-center rotate-[8deg] opacity-70">
          <Heart size={26} weight="fill" className="text-white" />
        </div>
        <div className="absolute bottom-[14%] left-[12%] w-14 h-14 rounded-2xl bg-[#FACC15] deck-tile flex items-center justify-center rotate-[14deg] opacity-70">
          <ArrowUpRight size={24} weight="bold" className="text-black" />
        </div>
        <div className="absolute bottom-[18%] right-[8%] w-16 h-16 rounded-2xl bg-white deck-tile flex items-center justify-center rotate-[-9deg] opacity-80">
          <Lightning size={28} weight="fill" className="text-[#2563EB]" />
        </div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div
          data-testid="login-card"
          className="glass-card rounded-3xl p-8 sm:p-10 shadow-[0_30px_80px_rgba(0,0,0,0.6)] animate-fade-up"
        >
          {/* Header: logo + brand */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-2xl bg-[#2563EB] flex items-center justify-center deck-tile">
              <Lightning size={26} weight="fill" className="text-white" />
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#FACC15]">
                PERSONAL DECK
              </div>
              <div className="text-xl font-heading font-black text-white">LaunchPad</div>
            </div>
          </div>

          {/* Demo tiles preview row */}
          <div
            data-testid="demo-tiles-row"
            className="grid grid-cols-6 gap-2 mb-7"
            aria-hidden="true"
          >
            {demoTiles.map((t, idx) => (
              <div
                key={idx}
                className="aspect-square rounded-xl deck-tile flex items-center justify-center"
                style={{
                  backgroundColor: t.color,
                  animationDelay: `${idx * 60}ms`,
                }}
              >
                <t.Icon
                  size={20}
                  weight="fill"
                  style={{ color: t.iconColor || "#FFFFFF" }}
                />
              </div>
            ))}
          </div>

          <button
            data-testid="google-login-btn"
            onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center gap-3 bg-white text-black font-heading font-bold text-base py-4 rounded-2xl deck-tile hover:bg-white/95 transition-all"
          >
            <GoogleLogo size={22} weight="bold" />
            Iniciar sesión con Google
          </button>

          <p className="mt-4 text-[11px] font-mono text-white/40 text-center leading-relaxed">
            Al continuar aceptas los{" "}
            <button
              data-testid="open-terms-btn"
              onClick={() => setPolicyOpen(true)}
              className="text-[#FACC15] hover:underline"
            >
              términos y políticas
            </button>
          </p>

          <div className="mt-5 pt-5 border-t border-white/10 flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.15em] text-white/40">
            <span>v1.0</span>
            <span>SECURE · OAUTH</span>
          </div>
        </div>
      </div>

      <PolicyDialog open={policyOpen} onAccept={handleAcceptPolicy} />
    </div>
  );
}
